import { RecipeType } from "@/types/baseTypes";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import styles from "./StepsControls.module.scss";
import { useEffect } from "react";

interface StepsControlProps {
  steps: RecipeType["steps_portion_big"] | RecipeType["steps_portion_small"];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  minStep?: number;
}

export const StepsControl = ({
  currentStep,
  setCurrentStep,
  steps,
  minStep = -3,
}: StepsControlProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const totalSteps = steps.length;
  const atEnd = currentStep >= totalSteps;
  const canGoPrev = currentStep >= minStep;
  const canGoNext = currentStep < totalSteps;

  const goPrev = () => {
    if (!canGoPrev) return;
    setCurrentStep(Math.max(minStep, currentStep - 1));
  };

  const goNext = () => {
    if (!canGoNext) return;
    setCurrentStep(Math.min(totalSteps, currentStep + 1));
  };

  // Accessibility: Ensure the controls are focusable and navigable via keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, totalSteps]);

  return (
    <div
      aria-controls="progressbar"
      aria-label={getLabel("cooking_mode_steps_nav", "Schrittnavigation")}
      aria-describedby="progress-status"
      className={`${styles["steps-box"]} is-flex is-align-items-center is-justify-content-space-between`}
    >
      <button
        className={`${styles["button-prev"]}`}
        type="button"
        onClick={goPrev}
        disabled={!canGoPrev}
        aria-label={getLabel("cooking_mode_return")}
      >
        <ReactSVG
          src={getIcon("circleArrowLeft")}
          wrapper={"svg"}
          width={24}
          height={24}
          aria-hidden
          beforeInjection={(svg) => {
            svg.setAttribute("aria-hidden", "true");
            svg.setAttribute("focusable", "false");
            svg.removeAttribute("aria-label");
            svg.removeAttribute("alt");
          }}
        />
        <span>{getLabel("cooking_mode_return")}</span>
      </button>
      <div aria-hidden="true">
        {currentStep >= 0 && currentStep < totalSteps && (
          <span className={styles["current-step"]}>
            {getLabel(
              "aria_label_cooking_mode_steps",
              'Schritt "${i}" von "${total}"',
            )
              .replace(/["`]?(\$\{i\}|\{i\})["`]?/g, String(currentStep + 1))
              .replace(
                /["`]?(\$\{total\}|\{total\})["`]?/g,
                String(totalSteps),
              )}
          </span>
        )}
        {atEnd && (
          <span className={styles["current-step"]}>
            {getLabel("done", "Geschafft")}
          </span>
        )}
      </div>

      <button
        type="button"
        className={`${styles["button-next"]} `}
        onClick={goNext}
        disabled={!canGoNext}
        aria-label={getLabel("cooking_mode_next")}
      >
        <span>{getLabel("cooking_mode_next")}</span>
        {/* jscpd:ignore-start */}
        <ReactSVG
          src={getIcon("circleArrowRight")}
          wrapper={"svg"}
          width={24}
          height={24}
          aria-hidden
          beforeInjection={(svg) => {
            svg.setAttribute("aria-hidden", "true");
            svg.setAttribute("focusable", "false");
            svg.removeAttribute("aria-label");
            svg.removeAttribute("alt");
          }}
        />
        {/* jscpd:ignore-end */}
      </button>
    </div>
  );
};
export default StepsControl;
